import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from 'prop-types';
import {
    getTrainings,
    getContentTypeLabel,
    getCategoryLabel,
    getContentTypeIcon,
    TRAINING_CATEGORIES,
    getUserPurchasedTrainings
} from "../../../services/trainingService";
import { auth, firestore } from "../../../config/firebase";
import firebase from "firebase/app";

const TrainingPage = ({ user, history }) => {
    // États
    const [trainings, setTrainings] = useState([]);
    const [purchasedTrainings, setPurchasedTrainings] = useState([]);
    const [activeCategory, setActiveCategory] = useState('all');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userTrainingAccess, setUserTrainingAccess] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    // Fonction pour extraire l'ID YouTube
    const extractYouTubeId = (url) => {
        if (!url) return null;
        const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
        const match = url.match(regex);
        return match ? match[1] : null;
    };

    // Catégories de formations
    const categories = [
        { id: 'all', name: 'Toutes', icon: 'pe-7s-menu' },
        { id: TRAINING_CATEGORIES.COURSE, name: 'Modules', icon: 'pe-7s-bookmarks' },
        { id: TRAINING_CATEGORIES.TD, name: 'Cas Pratiques', icon: 'pe-7s-note2' },
        { id: TRAINING_CATEGORIES.REFERENTIELS, name: 'Référentiels', icon: 'pe-7s-box2' },
        { id: TRAINING_CATEGORIES.INFOS_ALERTES, name: 'Infos Alertes', icon: 'pe-7s-bell' },
        { id: TRAINING_CATEGORIES.TP, name: 'Autres', icon: 'pe-7s-portfolio' },
    ];

    // Charger les accès aux formations de l'utilisateur depuis Firebase
    const loadUserTrainingAccess = async (userId) => {
        try {
            const userTrainingsRef = firestore.collection('userTrainings')
                .where('userId', '==', userId);

            const snapshot = await userTrainingsRef.get();
            const accessMap = {};

            snapshot.forEach(doc => {
                const data = doc.data();
                accessMap[data.trainingId] = {
                    id: doc.id,
                    ...data
                };
            });

            setUserTrainingAccess(accessMap);
            return accessMap;
        } catch (error) {
            return {};
        }
    };

    // Chargement des formations
    useEffect(() => {
        const loadTrainings = async () => {
            setIsLoading(true);
            try {
                // Vérifier l'utilisateur connecté
                const currentUser = user?.uid ? user : auth.currentUser;

                if (!currentUser) {
                    setError("Vous devez être connecté pour accéder aux formations");
                    setIsLoading(false);
                    return;
                }

                // Charger les accès utilisateur depuis Firebase
                const accessMap = await loadUserTrainingAccess(currentUser.uid);

                // Charger les formations disponibles
                const trainingsList = await getTrainings(null);
                setTrainings(trainingsList);

                // Charger les formations achetées par l'utilisateur
                const userPurchases = await getUserPurchasedTrainings(currentUser.uid);
                setPurchasedTrainings(userPurchases);

                setError(null);
            } catch (err) {
                setError("Impossible de charger les formations. Veuillez réessayer.");
            } finally {
                setIsLoading(false);
            }
        };

        loadTrainings();
    }, [user]);

    // Filtrage des formations
    const getFilteredTrainings = () => {
        // Appliquer d'abord le filtrage par catégorie (sauf 'purchased')
        let filtered = trainings;

        if (activeCategory !== 'all' && activeCategory !== 'purchased') {
            filtered = trainings.filter(training => training.category === activeCategory);
        }

        // Appliquer la recherche textuelle
        if (searchTerm) {
            filtered = filtered.filter(training =>
                training.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (training.summary && training.summary.toLowerCase().includes(searchTerm.toLowerCase())) ||
                getCategoryLabel(training.category).toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Si l'onglet "Mes Formations" est sélectionné, filtrer seulement les formations achetées/activées
        if (activeCategory === 'purchased') {
            filtered = filtered.filter(training => isTrainingPurchased(training.id));
        }

        return filtered;
    };

    const filteredTrainings = getFilteredTrainings();

    // Vérifier si une formation a été achetée (payante ou gratuite)
    const isTrainingPurchased = (trainingId) => {
        // Vérifier dans les formations achetées via le service
        const isInPurchased = purchasedTrainings.some(training => training.id === trainingId);

        // Vérifier dans les accès utilisateur (formations gratuites)
        const isInUserAccess = userTrainingAccess[trainingId];

        return isInPurchased || isInUserAccess;
    };

    // Vérifier si une formation est gratuite
    const isFreeTraining = (training) => {
        return training && training.price === 0;
    };

    // Vérifier si l'utilisateur a déjà accédé à une formation gratuite
    const hasUserAccessedFreeTraining = (trainingId) => {
        return !!userTrainingAccess[trainingId];
    };

    // Obtenir le statut d'achat
    const getPurchaseStatus = (training) => {
        if (isFreeTraining(training)) {
            return hasUserAccessedFreeTraining(training.id) ? 'free_accessed' : 'free_available';
        }
        return isTrainingPurchased(training.id) ? 'purchased' : 'not_purchased';
    };

    // Rediriger vers la page de détail d'une formation
    const handleViewTraining = (trainingId) => {
        history.push(`/app/training/detail/${trainingId}`);
    };

    // Télécharger directement une formation gratuite
    const handleDownloadFreeTraining = async (training) => {
        try {
            const currentUser = user?.uid ? user : auth.currentUser;

            if (!currentUser) {
                alert("Vous devez être connecté pour télécharger cette formation");
                return;
            }

            // Vérifier si l'utilisateur a déjà accédé à cette formation
            if (hasUserAccessedFreeTraining(training.id)) {
                // Si déjà accédé, ouvrir le PDF directement
                if (training.fileUrl) {
                    window.open(training.fileUrl, '_blank');
                } else {
                    handleViewTraining(training.id);
                }
                return;
            }

            // Enregistrer l'accès gratuit dans la base de données
            const userTrainingRef = firestore.collection('userTrainings').doc();
            await userTrainingRef.set({
                userId: currentUser.uid,
                trainingId: training.id,
                trainingTitle: training.title,
                trainingCategory: training.category,
                trainingContentType: training.contentType,
                trainingSummary: training.summary,
                trainingFileUrl: training.fileUrl,
                trainingFileName: training.fileName,
                trainingYoutubeUrl: training.youtubeUrl,
                purchaseDate: firebase.firestore.FieldValue.serverTimestamp(),
                price: 0,
                status: 'completed',
                isFree: true,
                createdAt: firebase.firestore.FieldValue.serverTimestamp()
            });

            // Mettre à jour l'état local
            setUserTrainingAccess(prev => ({
                ...prev,
                [training.id]: {
                    id: userTrainingRef.id,
                    userId: currentUser.uid,
                    trainingId: training.id,
                }
            }));

            // Ouvrir le PDF directement
            if (training.fileUrl) {
                window.open(training.fileUrl, '_blank');
            } else {
                handleViewTraining(training.id);
            }

        } catch (error) {
            alert("Erreur lors du téléchargement. Veuillez réessayer.");
        }
    };

    // Formater le prix
    const formatPrice = (price) => {
        if (price === 0) {
            return "Gratuit";
        }
        return `${new Intl.NumberFormat("fr-FR").format(price)} FCFA`;
    };

    // Obtenir le texte et l'icône du bouton
    const getButtonConfig = (training) => {
        const status = getPurchaseStatus(training);

        switch (status) {
            case 'purchased':
                return {
                    text: "Consulter",
                    icon: "pe-7s-play",
                    className: "btn-success",
                    onClick: () => handleViewTraining(training.id)
                };

            case 'free_accessed':
                return {
                    text: "Télécharger",
                    icon: "pe-7s-download",
                    className: "btn-info",
                    onClick: () => {
                        if (training.fileUrl) {
                            window.open(training.fileUrl, '_blank');
                        } else {
                            handleViewTraining(training.id);
                        }
                    }
                };

            case 'free_available':
                return {
                    text: "Télécharger",
                    icon: "pe-7s-download",
                    className: "btn-info",
                    onClick: () => handleDownloadFreeTraining(training)
                };

            default: // 'not_purchased'
                return {
                    text: "Acheter",
                    icon: "pe-7s-cart",
                    className: "btn-primary",
                    onClick: () => history.push(`/app/training/purchase/${training.id}`)
                };
        }
    };

    return (
        <div className="training-page">
            <div className="section-header">
                <div className="header-content">
                    <h1 className="title">Formations médicales</h1>
                    <p className="subtitle">
                        Développez vos connaissances grâce à des modules, cas pratiques et référentiels conçus pour les professionnels de santé.
                    </p>
                </div>
                <div className="search-container">
                    <div className="search-input-wrapper">
                        <i className="pe-7s-search search-icon"></i>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Rechercher une formation..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="clear-search" onClick={() => setSearchTerm('')}>
                                <i className="pe-7s-close"></i>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger">
                    <i className="pe-7s-attention"></i>
                    {error}
                </div>
            )}

            <div className="category-tabs-container">
                <div className="category-tabs">
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
                            onClick={() => setActiveCategory(category.id)}
                        >
                            <i className={category.icon}></i>
                            <span>{category.name}</span>
                        </button>
                    ))}
                </div>
                <div
                    className="results-count"
                >
                    {(() => {
                        const counts = filteredTrainings.reduce((acc, training) => {
                            acc[training.category] = (acc[training.category] || 0) + 1;
                            return acc;
                        }, {});

                        const parts = [];
                        if (counts[TRAINING_CATEGORIES.COURSE]) {
                            parts.push(`${counts[TRAINING_CATEGORIES.COURSE]} module${counts[TRAINING_CATEGORIES.COURSE] > 1 ? 's' : ''}`);
                        }
                        if (counts[TRAINING_CATEGORIES.TD]) {
                            parts.push(`${counts[TRAINING_CATEGORIES.TD]} cas pratique${counts[TRAINING_CATEGORIES.TD] > 1 ? 's' : ''}`);
                        }
                        if (counts[TRAINING_CATEGORIES.REFERENTIELS]) {
                            parts.push(`${counts[TRAINING_CATEGORIES.REFERENTIELS]} référentiel${counts[TRAINING_CATEGORIES.REFERENTIELS] > 1 ? 's' : ''}`);
                        }
                        if (counts[TRAINING_CATEGORIES.INFOS_ALERTES]) {
                            parts.push(`${counts[TRAINING_CATEGORIES.INFOS_ALERTES]} alerte${counts[TRAINING_CATEGORIES.INFOS_ALERTES] > 1 ? 's' : ''}`);
                        }
                        if (counts[TRAINING_CATEGORIES.TP]) {
                            parts.push(`${counts[TRAINING_CATEGORIES.TP]} autre${counts[TRAINING_CATEGORIES.TP] > 1 ? 's' : ''}`);
                        }

                        const prefix = activeCategory === 'purchased'
                            ? 'Achetée(s) ou téléchargée(s)'
                            : 'Disponible(s)';

                        return parts.length > 0
                            ? `${prefix} : ${parts.join(', ')}`
                            : 'Aucune formation disponible';
                    })()}
                </div>
            </div>

            {isLoading ? (
                <div className="loading-container">
                    <div className="spinner-container">
                        <div className="spinner"></div>
                    </div>
                    <p className="loading-text">Chargement des formations...</p>
                </div>
            ) : filteredTrainings.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon-container">
                        <i className="pe-7s-notebook"></i>
                    </div>
                    <h3 className="empty-title">Aucune formation trouvée</h3>
                    <p className="empty-message">
                        {searchTerm ?
                            `Aucun résultat pour "${searchTerm}"` :
                            activeCategory === 'purchased' ?
                                'Vous n\'avez encore acheté ou téléchargé aucune formation. Explorez notre catalogue pour commencer !' :
                                'Aucune formation disponible dans cette catégorie'
                        }
                    </p>
                    {searchTerm && (
                        <button
                            className="btn btn-outline"
                            onClick={() => setSearchTerm('')}
                        >
                            Réinitialiser la recherche
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div className="trainings-grid">
                        {filteredTrainings.map((training) => {
                            const buttonConfig = getButtonConfig(training);
                            const status = getPurchaseStatus(training);
                            const purchased = status === 'purchased';
                            const freeAccessed = status === 'free_accessed';
                            const freeAvailable = status === 'free_available';

                            return (
                                <div className={`training-card ${purchased ? 'purchased' : ''} ${freeAccessed ? 'free-accessed' : ''}`} key={training.id}>
                                    {/* Badges */}
                                    <div className="card-badges">
                                        {freeAvailable && (
                                            <div className="badge free-badge">
                                                <i className="pe-7s-gift"></i>
                                                Gratuit
                                            </div>
                                        )}
                                        {freeAccessed && (
                                            <div className="badge accessed-badge">
                                                <i className="pe-7s-check"></i>
                                                Déjà téléchargé
                                            </div>
                                        )}
                                        {purchased && (
                                            <div className="badge purchased-badge">
                                                <i className="pe-7s-star"></i>
                                                Acheté
                                            </div>
                                        )}
                                    </div>

                                    {/* Aperçu visuel */}
                                    <div className="training-preview">
                                        {training.contentType === 'video' && training.youtubeUrl ? (
                                            <div className="youtube-thumbnail">
                                                <img
                                                    src={`https://img.youtube.com/vi/${extractYouTubeId(training.youtubeUrl)}/maxresdefault.jpg`}
                                                    alt={`Aperçu de ${training.title}`}
                                                    onError={(e) => {
                                                        e.target.src = `https://img.youtube.com/vi/${extractYouTubeId(training.youtubeUrl)}/hqdefault.jpg`;
                                                    }}
                                                />
                                                <div className="play-overlay">
                                                    <i className="pe-7s-play"></i>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="content-preview">
                                                <div className="preview-icon">
                                                    <i className={getContentTypeIcon(training.contentType)}></i>
                                                </div>
                                                <div className="preview-text">
                                                    <span>{getContentTypeLabel(training.contentType)}</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="training-card-content">
                                        <div className="card-header">
                                            <span className="training-category">
                                                <i className="pe-7s-folder"></i>
                                                {getCategoryLabel(training.category)}
                                            </span>
                                            <span className="training-type">
                                                <i className={getContentTypeIcon(training.contentType)}></i>
                                                {getContentTypeLabel(training.contentType)}
                                            </span>
                                        </div>

                                        <div className="card-body">
                                            <h3 className="training-title" style={{ fontSize: '18px', fontWeight: '700' }}>
                                                {training.title}
                                            </h3>
                                            <p className="training-summary" style={{ fontSize: '14px' }}>
                                                {training.summary}
                                            </p>
                                        </div>

                                        <div className="card-footer">
                                            <div className="price-section">
                                                <div className={`training-price ${training.price === 0 ? 'free-price' : ''}`}>
                                                    {formatPrice(training.price)}
                                                </div>
                                                {training.duration && (
                                                    <div className="training-duration">
                                                        <i className="pe-7s-clock"></i>
                                                        {training.duration} min
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                className={`btn ${buttonConfig.className} ${purchased ? 'btn-purchased' : ''}`}
                                                onClick={buttonConfig.onClick}
                                            >
                                                <i className={buttonConfig.icon}></i>
                                                <span>{buttonConfig.text}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            <style jsx>{`
                .training-page {
                    padding: 40px 50px;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
                    min-height: 100vh;
                }

                .section-header {
                    margin-bottom: 3rem;
                    position: relative;
                }

                .header-content {
                    text-align: center;
                    margin-bottom: 2.5rem;
                }

                .title {
                    color: #2d3748;
                    font-size: 3.5rem;
                    margin-bottom: 1.2rem;
                    font-weight: 800;
                    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .subtitle {
                    color: #4b5563;
                    font-size: 1.4rem;
                    max-width: 800px;
                    margin: 0 auto;
                    line-height: 1.7;
                    font-weight: 400;
                }

                .search-container {
                    max-width: 600px;
                    margin: 0 auto;
                }

                .search-input-wrapper {
                    position: relative;
                    background: white;
                    border-radius: 16px;
                    padding: 12px 24px;
                    box-shadow: 0 6px 25px rgba(0,0,0,0.1);
                    transition: all 0.3s ease;
                    border: 2px solid #e5e7eb;
                }

                .search-input-wrapper:focus-within {
                    box-shadow: 0 10px 35px rgba(96, 165, 250, 0.25);
                    transform: translateY(-3px);
                    border-color: #60a5fa;
                }

                .search-icon {
                    position: absolute;
                    left: 24px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #9ca3af;
                    font-size: 1.4rem;
                }

                .search-input {
                    width: 100%;
                    padding: 16px 50px 16px 55px;
                    border: none;
                    background: transparent;
                    font-size: 1.2rem;
                    color: #2d3748;
                    outline: none;
                    font-weight: 500;
                }

                .search-input::placeholder {
                    color: #9ca3af;
                    font-weight: 400;
                }

                .clear-search {
                    position: absolute;
                    right: 20px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    color: #9ca3af;
                    cursor: pointer;
                    padding: 8px;
                    transition: color 0.2s;
                    font-size: 1.2rem;
                }

                .clear-search:hover {
                    color: #ef4444;
                }

                .category-tabs-container {
                    margin-bottom: 3.5rem;
                }

                .category-tabs {
                    display: flex;
                    justify-content: center;
                    gap: 15px;
                    margin-bottom: 1.2rem;
                    flex-wrap: wrap;
                }

                .category-tab {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background: white;
                    border: 2px solid #e5e7eb;
                    padding: 16px 28px;
                    border-radius: 14px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    color: #4b5563;
                    font-weight: 600;
                    font-size: 1.1rem;
                    box-shadow: 0 3px 12px rgba(0,0,0,0.08);
                    min-height: 60px;
                }

                .category-tab:hover {
                    background: #f3f4f6;
                    transform: translateY(-3px);
                    box-shadow: 0 6px 20px rgba(0,0,0,0.12);
                    border-color: #60a5fa;
                }

                .category-tab.active {
                    background: #60a5fa;
                    color: white;
                    box-shadow: 0 6px 25px rgba(96, 165, 250, 0.5);
                    transform: translateY(-3px);
                    border-color: #3b82f6;
                }

                .category-tab i {
                    font-size: 1.3rem;
                }

                .results-count {
                    text-align: center;
                    color: #6b7280;
                    font-size: 1.25rem;
                    font-weight: 600;
                }

                .trainings-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
                    gap: 24px;
                    margin: 0 auto;
                }

                .training-card {
                    background: white;
                    border-radius: 16px;
                    overflow: hidden;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.09);
                    border: 1px solid #e5e7eb;
                }

                .training-card:hover {
                    transform: translateY(-6px);
                    box-shadow: 0 16px 32px rgba(0,0,0,0.13);
                }

                .training-card.purchased {
                    border-color: #10b981;
                    background: linear-gradient(135deg, #f0fff4 0%, #dcfce7 100%);
                }

                .training-card.free-accessed {
                    border-color: #06b6d4;
                    background: linear-gradient(135deg, #ecfeff 0%, #cffafe 100%);
                }

                .card-badges {
                    position: absolute;
                    top: 14px;
                    right: 14px;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    z-index: 2;
                }

                .badge {
                    padding: 6px 13px;
                    border-radius: 18px;
                    font-size: 0.78rem;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    backdrop-filter: blur(10px);
                    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
                }

                .free-badge {
                    color: #2563eb;
                    background: linear-gradient(135deg, rgba(96, 165, 250, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%);
                    border: 1px solid rgba(59, 130, 246, 0.3);
                }

                .accessed-badge {
                    color: #059669;
                    background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%);
                    border: 1px solid rgba(16, 185, 129, 0.3);
                }

                .purchased-badge {
                    color: #ffffff;
                    background: linear-gradient(135deg, #000000 0%, #374151 100%);
                    border: 1px solid rgba(0, 0, 0, 0.3);
                }

                .training-preview {
                    position: relative;
                    height: 165px;
                    overflow: hidden;
                    background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
                }

                .training-card.purchased .training-preview {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                }

                .youtube-thumbnail {
                    position: relative;
                    width: 100%;
                    height: 100%;
                }

                .youtube-thumbnail img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 0.6s ease;
                }

                .training-card:hover .youtube-thumbnail img {
                    transform: scale(1.12);
                }

                .play-overlay {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    background: rgba(255, 255, 255, 0.95);
                    color: #ef4444;
                    width: 58px;
                    height: 58px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.6rem;
                    transition: all 0.3s ease;
                    backdrop-filter: blur(5px);
                }

                .training-card:hover .play-overlay {
                    background: #ef4444;
                    color: white;
                    transform: translate(-50%, -50%) scale(1.15);
                }

                .content-preview {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 100%;
                    color: white;
                    text-align: center;
                    padding: 18px;
                }

                .preview-icon {
                    font-size: 2.5rem;
                    margin-bottom: 12px;
                    opacity: 0.9;
                    filter: drop-shadow(0 4px 6px rgba(0,0,0,0.25));
                }

                .preview-text {
                    font-size: 0.85rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1.2px;
                    opacity: 0.95;
                }

                .training-card-content {
                    padding: 18px 20px;
                }

                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 14px;
                    gap: 8px;
                    flex-wrap: wrap;
                }

                .training-category,
                .training-type {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 0.85rem;
                    font-weight: 700;
                    color: #4b5563;
                }

                .training-category {
                    background: #f3f4f6;
                    padding: 5px 11px;
                    border-radius: 8px;
                }

                .training-card.purchased .training-category {
                    background: rgba(16, 185, 129, 0.1);
                    color: #059669;
                }

                .training-type {
                    color: #60a5fa;
                }

                .training-card.purchased .training-type {
                    color: #10b981;
                }

                .card-body {
                    margin-bottom: 16px;
                }

                .training-title {
                    margin: 0 0 10px 0;
                    color: #1f2937;
                    font-size: 1.18rem;
                    font-weight: 800;
                    line-height: 1.35;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .training-summary {
                    color: #6b7280;
                    font-size: 1.05rem;
                    line-height: 1.6;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    font-weight: 400;
                }

                .card-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding-top: 16px;
                    border-top: 1px solid #f3f4f6;
                    gap: 10px;
                }

                .training-card.purchased .card-footer {
                    border-top-color: rgba(16, 185, 129, 0.2);
                }

                .price-section {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                }

                .training-price {
                    font-weight: 900;
                    color: #1f2937;
                    font-size: 1.3rem;
                }

                .free-price {
                    color: #60a5fa;
                }

                .training-card.purchased .training-price {
                    color: #059669;
                }

                .training-duration {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 0.85rem;
                    color: #9ca3af;
                    font-weight: 500;
                }

                .training-card.purchased .training-duration {
                    color: #6ee7b7;
                }

                .btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 11px 20px;
                    border: none;
                    border-radius: 11px;
                    font-weight: 700;
                    font-size: 0.92rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    color: white;
                    min-height: 44px;
                    flex-wrap: nowrap;
                    white-space: nowrap;
                }

                .btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
                }

                .btn i {
                    font-size: 1.3rem;
                    flex-shrink: 0;
                }

                .btn span {
                    flex-shrink: 0;
                }

                .btn-primary {
                    background: #3b82f6;
                }

                .btn-primary:hover {
                    background: #2563eb;
                    box-shadow: 0 10px 30px rgba(59, 130, 246, 0.4);
                }

                .btn-success {
                    background: #10b981;
                }

                .btn-success:hover {
                    background: #059669;
                    box-shadow: 0 10px 30px rgba(16, 185, 129, 0.4);
                }

                .btn-info {
                    background: #06b6d4;
                }

                .btn-info:hover {
                    background: #0891b2;
                    box-shadow: 0 10px 30px rgba(6, 182, 212, 0.4);
                }

                /* Nouveau style pour le bouton des formations achetées */
                .btn-purchased {
                    background: linear-gradient(135deg, #000000 0%, #374151 100%);
                    color: #ffffff;
                    border: 2px solid #10b981;
                }

                .btn-purchased:hover {
                    background: linear-gradient(135deg, #374151 0%, #000000 100%);
                    box-shadow: 0 10px 30px rgba(16, 185, 129, 0.4);
                    transform: translateY(-3px);
                }

                .btn-outline {
                    background: transparent;
                    border: 3px solid #60a5fa;
                    color: #60a5fa;
                    padding: 14px 28px;
                }

                .btn-outline:hover {
                    background: #60a5fa;
                    color: white;
                    box-shadow: 0 10px 30px rgba(96, 165, 250, 0.4);
                }

                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 5rem;
                }

                .spinner-container {
                    position: relative;
                    width: 80px;
                    height: 80px;
                }

                .spinner {
                    border: 5px solid rgba(96, 165, 250, 0.1);
                    border-left-color: #60a5fa;
                    border-right-color: #3b82f6;
                    border-radius: 50%;
                    width: 100%;
                    height: 100%;
                    animation: spin 1.5s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .loading-text {
                    margin-top: 25px;
                    color: #4b5563;
                    font-size: 1.3rem;
                    font-weight: 600;
                }

                .empty-state {
                    text-align: center;
                    padding: 5rem;
                    background: white;
                    border-radius: 22px;
                    box-shadow: 0 6px 25px rgba(0,0,0,0.1);
                    margin: 3rem auto;
                    max-width: 550px;
                    border: 2px solid #e5e7eb;
                }

                .empty-icon-container {
                    font-size: 6rem;
                    color: #d1d5db;
                    margin-bottom: 2rem;
                }

                .empty-title {
                    color: #1f2937;
                    font-size: 2.2rem;
                    margin-bottom: 1.2rem;
                    font-weight: 800;
                }

                .empty-message {
                    color: #6b7280;
                    font-size: 1.3rem;
                    margin-bottom: 2.5rem;
                    line-height: 1.7;
                }

                .alert {
                    background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
                    color: white;
                    padding: 20px 28px;
                    border-radius: 14px;
                    margin-bottom: 3rem;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.25);
                    font-size: 1.1rem;
                    font-weight: 500;
                    border: 2px solid #fca5a5;
                }

                .alert i {
                    font-size: 1.4rem;
                }

                @media (max-width: 1200px) {
                    .trainings-grid {
                        grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
                    }

                    .title {
                        font-size: 3rem;
                    }

                    .subtitle {
                        font-size: 1.3rem;
                    }
                }

                @media (max-width: 768px) {
                    .training-page {
                        padding: 30px;
                    }

                    .title {
                        font-size: 2.5rem;
                    }

                    .subtitle {
                        font-size: 1.2rem;
                    }

                    .category-tabs {
                        gap: 10px;
                    }

                    .category-tab {
                        padding: 14px 20px;
                        font-size: 1rem;
                        min-height: 55px;
                    }

                    .btn {
                        padding: 12px 20px;
                        font-size: 0.9rem;
                        min-height: 44px;
                    }

                    .training-card-content {
                        padding: 18px 20px;
                    }
                }

                @media (max-width: 480px) {
                    .trainings-grid {
                        grid-template-columns: 1fr;
                    }

                    .category-tabs {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 8px;
                    }

                    .category-tab {
                        width: 100%;
                        justify-content: center;
                        padding: 16px;
                    }

                    .title {
                        font-size: 2.2rem;
                    }

                    .subtitle {
                        font-size: 1.1rem;
                    }

                    .btn {
                        padding: 12px 20px;
                        font-size: 0.95rem;
                    }
                }

                .training-page {
                    padding: 30px clamp(18px, 4vw, 56px) 48px;
                    color: #263746;
                    background: #f6f9fa;
                }

                .training-page .section-header {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) minmax(280px, 430px);
                    align-items: center;
                    gap: 32px;
                    max-width: 1600px;
                    margin: 0 auto 26px;
                    padding: 30px 34px;
                    background: linear-gradient(120deg, #fff 0%, #f2fbfd 100%);
                    border: 1px solid #e4eef1;
                    border-radius: 18px;
                    box-shadow: 0 8px 24px rgba(31, 58, 69, 0.05);
                }

                .training-page .header-content {
                    margin: 0;
                    text-align: left;
                }

                .training-page .title {
                    margin: 0 0 8px;
                    color: #17313d;
                    background: none;
                    font-size: clamp(25px, 2.4vw, 34px);
                    font-weight: 700;
                    letter-spacing: -0.5px;
                    text-shadow: none;
                    -webkit-text-fill-color: currentColor;
                }

                .training-page .subtitle {
                    max-width: 760px;
                    margin: 0;
                    color: #64748b;
                    font-size: 14px;
                    line-height: 1.65;
                }

                .training-page .search-container {
                    width: 100%;
                    max-width: none;
                    margin: 0;
                }

                .training-page .search-input-wrapper {
                    padding: 3px;
                    border: 1px solid #dce8ec;
                    border-radius: 10px;
                    box-shadow: 0 3px 10px rgba(31, 58, 69, 0.04);
                }

                .training-page .search-input-wrapper:focus-within {
                    border-color: var(--app-primary-color);
                    box-shadow: 0 0 0 3px rgba(var(--app-primary-rgb), 0.14);
                    transform: none;
                }

                .training-page .search-input {
                    padding: 12px 42px 12px 42px;
                    font-size: 14px;
                }

                .training-page .search-icon {
                    left: 15px;
                    font-size: 18px;
                }

                .training-page .category-tabs-container {
                    max-width: 1600px;
                    margin: 0 auto 24px;
                }

                .training-page .category-tabs {
                    justify-content: flex-start;
                    gap: 8px;
                    margin-bottom: 14px;
                }

                .training-page .category-tab {
                    min-height: 42px;
                    padding: 9px 15px;
                    color: #526574;
                    background: #fff;
                    border: 1px solid #dfe8eb;
                    border-radius: 9px;
                    box-shadow: none;
                    font-size: 13px;
                    gap: 8px;
                }

                .training-page .category-tab:hover {
                    color: #087f95;
                    background: #f2fbfd;
                    border-color: var(--app-primary-color);
                    box-shadow: none;
                    transform: translateY(-1px);
                }

                .training-page .category-tab.active {
                    color: #073b46;
                    background: var(--app-primary-color);
                    border-color: var(--app-primary-color);
                    box-shadow: 0 4px 12px rgba(var(--app-primary-rgb), 0.2);
                    transform: none;
                }

                .training-page .category-tab span {
                    font-size: 13px !important;
                }

                .training-page .results-count {
                    color: #64748b;
                    font-size: 13px !important;
                    font-weight: 500;
                    text-align: left;
                }

                .training-page .trainings-grid {
                    max-width: 1600px;
                }

                .training-page .training-card {
                    border-color: #e3ebee;
                    border-radius: 14px;
                    box-shadow: 0 5px 18px rgba(31, 58, 69, 0.05);
                }

                .training-page .training-card:hover {
                    box-shadow: 0 12px 26px rgba(31, 58, 69, 0.1);
                    transform: translateY(-3px);
                }

                .training-page .training-preview,
                .training-page .training-card:not(.purchased) .training-preview {
                    background: var(--app-primary-color);
                }

                .training-page .training-type,
                .training-page .free-price {
                    color: #087f95;
                }

                .training-page .btn-primary,
                .training-page .btn-primary:hover {
                    color: #073b46;
                    background: var(--app-primary-color);
                    box-shadow: 0 4px 12px rgba(var(--app-primary-rgb), 0.18);
                }

                .training-page .empty-state {
                    max-width: 760px;
                    margin: 24px auto;
                    padding: 52px 28px;
                    border: 1px solid #e3ebee;
                    border-radius: 16px;
                    box-shadow: 0 8px 24px rgba(31, 58, 69, 0.05);
                }

                .training-page .loading-container {
                    color: #64748b;
                }

                @media (max-width: 768px) {
                    .training-page {
                        padding: 18px 14px 32px;
                    }

                    .training-page .section-header {
                        grid-template-columns: minmax(0, 1fr);
                        gap: 18px;
                        padding: 22px 18px;
                    }

                    .training-page .header-content {
                        text-align: left;
                    }

                    .training-page .category-tabs {
                        justify-content: flex-start;
                        flex-direction: row;
                        overflow-x: auto;
                        padding-bottom: 4px;
                    }

                    .training-page .category-tab {
                        width: auto;
                        flex: 0 0 auto;
                        justify-content: center;
                    }
                }
            `}</style>
        </div>
    );
};

TrainingPage.propTypes = {
    user: PropTypes.shape({
        uid: PropTypes.string,
        email: PropTypes.string,
        displayName: PropTypes.string
    }),
    history: PropTypes.object.isRequired
};

export default withRouter(TrainingPage);